import styled from '@emotion/styled';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '../../../assets/icons';
import { KpiState } from '../../../helpers/kpi';
import { theme } from '../../../theme';
import { Badge } from '../../badge/Badge';
import { BodyMedium, HeadingMedium } from '../../Typography';
import { badgeStateToColor, getScoreColor, KpiCardIconButton } from '../kpiCardShared';

export type KpiScoreCardProps = {
  title: string;
  subtext?: string;
  /** Main value to display, e.g. `3.82` or `'75%'`. */
  value: string | number;
  /** Muted suffix shown next to the value, e.g. `/4`. */
  valueSuffix?: string;
  /** Colour state driving the value and badge colour. */
  state?: KpiState;
  /** Badge text — omit to hide the badge. */
  badgeText?: string;
  /** Called when the round "navigate to detail" button is clicked; the button is hidden without it. */
  onDetailClick?: () => void;
  /** Accessible label for the detail button. */
  detailButtonLabel?: string;
  detailRows?: React.ReactNode;
  className?: string;
  'data-cy'?: string;
  'data-icom'?: string;
};

/**
 * Compact variant of `KpiCard` without the progress bar or detail action: title
 * and description on the left, score and badge on the right
 */
export const KpiScoreCard: FC<KpiScoreCardProps> = ({
  title,
  subtext,
  value,
  valueSuffix,
  state = 'normal',
  badgeText,
  onDetailClick,
  detailButtonLabel,
  detailRows,
  className,
  'data-cy': dataCy = 'kpi-score-card',
  'data-icom': dataIcom,
}) => {
  const { t } = useTranslation('kpiCard');
  const valueColor = getScoreColor(state);
  const hasDetails = !!detailRows;

  return (
    <CardWrapper className={className} data-cy={dataCy} data-icom={dataIcom}>
      <HeaderRow>
        <TextColumn>
          <HeadingMedium margin="0" bold data-cy={`${dataCy}-title`}>
            {title}
          </HeadingMedium>
          {(subtext || hasDetails) && (
            <BodyMedium margin="0" color={theme.colors.text.secondary} data-cy={`${dataCy}-subtext`}>
              {subtext}
            </BodyMedium>
          )}
        </TextColumn>
        <ScoreColumn>
          <ScoreRow>
            <HeadingMedium margin="0" bold data-cy={`${dataCy}-score`} color={valueColor}>
              {value}
            </HeadingMedium>
            {valueSuffix && (
              <BodyMedium margin="0" color={theme.colors.text.secondary} data-cy={`${dataCy}-score-suffix`}>
                {valueSuffix}
              </BodyMedium>
            )}
          </ScoreRow>
          {badgeText && (
            <Badge text={badgeText} color={badgeStateToColor(state)} subtle size="Small" data-cy={`${dataCy}-badge`} />
          )}
        </ScoreColumn>
        {onDetailClick && (
          <KpiCardIconButton
            type="button"
            onClick={onDetailClick}
            aria-label={detailButtonLabel || t('detailButtonLabel')}
            data-cy={`${dataCy}-detail-button`}
            data-icom="kpi-card-detail-button"
          >
            <ChevronRightIcon />
          </KpiCardIconButton>
        )}
      </HeaderRow>
      {hasDetails && <div data-cy={`${dataCy}-detail`}>{detailRows}</div>}
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.f};
  padding: ${theme.spacing.g};
  background-color: ${theme.colors.backgroundCover.default};
  border-radius: ${theme.borderRadius.l};
  width: 100%;
  margin-bottom: ${theme.spacing.f};
  margin-right: ${theme.spacing.f};
  border: 1px solid #ededed;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.f};
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.e};
  min-width: 0;
  width: 100%;
`;

const ScoreColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing.d};
  flex: 0 0 auto;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.c};
`;
